import styled from 'styled-components';
import { Input } from '@shared/ui/Input';

export const Tiles = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: ${({ theme }) => theme.spacing(3)};
`;

const tileBox = `
  text-align: left;
  font: inherit;
`;

export const Tile = styled.div`
  ${tileBox}
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.backgroundElevated};
`;

export const TileButton = styled.button<{ $active: boolean }>`
  ${tileBox}
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(3)};
  background: ${({ $active, theme }) => ($active ? theme.colors.primarySoft : theme.colors.backgroundElevated)};
  cursor: pointer;

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

export const TileValue = styled.span`
  display: block;
  font-size: 20px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const TileLabel = styled.span`
  display: block;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: ${({ theme }) => theme.spacing(0.5)};
`;

/** Оговорки к итогам — сразу под плитками, а не свёрнутыми внизу. */
export const Notice = styled.div`
  margin-top: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => theme.spacing(3)};
  border: 1px solid ${({ theme }) => theme.colors.status.warning};
  border-left-width: 4px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
  line-height: 1.5;

  & ul {
    margin: ${({ theme }) => theme.spacing(1)} 0 0;
    padding-left: ${({ theme }) => theme.spacing(5)};
  }
`;

export const SectionHead = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
`;

export const Toggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

export const SectionBar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

export const SectionButton = styled.button<{ $active: boolean }>`
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(3)}`};
  border: none;
  border-bottom: 2px solid ${({ $active, theme }) => ($active ? theme.colors.primary : 'transparent')};
  background: none;
  font: inherit;
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  color: ${({ $active, theme }) => ($active ? theme.colors.text.primary : theme.colors.text.secondary)};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: -2px;
  }
`;

export const Filters = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

export const SearchWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 200px;
  max-width: 420px;

  & > svg {
    position: absolute;
    left: ${({ theme }) => theme.spacing(2.5)};
    top: 50%;
    transform: translateY(-50%);
    color: ${({ theme }) => theme.colors.text.muted};
    pointer-events: none;
  }

  & > input {
    padding-left: ${({ theme }) => theme.spacing(8)};
  }
`;

export const CheckLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;

  & > input {
    accent-color: ${({ theme }) => theme.colors.primary};
  }
`;

export const Counter = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const Alert = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(2.5)};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.status.danger};
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
`;

export const TableScroll = styled.div`
  overflow: auto;
  max-height: 70vh;
  -webkit-overflow-scrolling: touch;

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    text-align: left;
    padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(2.5)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
  }

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.colors.text.secondary};
    background: ${({ theme }) => theme.colors.surface};
    border-bottom: none;
    box-shadow: inset 0 -1px 0 ${({ theme }) => theme.colors.border};
    white-space: nowrap;
  }

  th.num,
  td.num {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
`;

/** Строка «проверить» — мягкий тёплый фон в тон бейджу (красный тон бренда
 *  читался бы как ошибка). */
export const Tr = styled.tr<{ $review: boolean }>`
  background: ${({ $review, theme }) => ($review ? `${theme.colors.status.warning}14` : 'transparent')};
`;

export const Name = styled.div<{ $excluded?: boolean }>`
  font-weight: 500;
  color: ${({ $excluded, theme }) => ($excluded ? theme.colors.text.secondary : theme.colors.text.primary)};
  text-decoration: ${({ $excluded }) => ($excluded ? 'line-through' : 'none')};
  overflow-wrap: anywhere;
`;

export const Small = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: ${({ theme }) => theme.spacing(0.5)};
  overflow-wrap: anywhere;
`;

/** Почему строка «проверить» — под наименованием. */
export const Reasons = styled.ul`
  margin: ${({ theme }) => theme.spacing(1)} 0 0;
  padding-left: ${({ theme }) => theme.spacing(4)};
  font-size: 12px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.text.secondary};
  overflow-wrap: anywhere;
`;

/** Кратность под количеством — узким блоком с переносом, чтобы таблица не
 *  расползлась вбок (ячейка количества не переносит строки). */
export const MultNoteBox = styled.div<{ $found: boolean }>`
  margin: ${({ theme }) => theme.spacing(0.5)} 0 0 auto;
  max-width: 130px;
  white-space: normal;
  font-size: 12px;
  line-height: 1.35;
  color: ${({ $found, theme }) => ($found ? theme.colors.text.primary : theme.colors.text.secondary)};
  overflow-wrap: anywhere;
`;

/** Источник строки бывает на сотни знаков (все листы всех документов) —
 *  две строки, полностью — во всплывающей подсказке. */
export const Source = styled(Small)`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const Decision = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(1.5)};
  min-width: 150px;
`;

export const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1.5)};
  flex-wrap: wrap;
`;

export const Muted = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const More = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

export const Notes = styled.ul`
  margin: ${({ theme }) => theme.spacing(3)} 0 0;
  padding-left: ${({ theme }) => theme.spacing(5)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const DocsTable = styled(Table)`
  margin-top: ${({ theme }) => theme.spacing(3)};

  th {
    position: static;
  }
`;

export const MultForm = styled.form`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  flex-wrap: wrap;
`;

export const MultInput = styled(Input)`
  width: 78px;
  height: 30px;
  font-size: 12.5px;
`;
